import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PeriodFilterModal,
  SingleSelectFilterModal,
  TableFilterBar,
  TableFilterButton,
  TableFilterClearLink,
  hasActiveClearableFilters,
} from "../../../ui/components/filters";
import { getFilterLabel } from "../../activity/utils/activityPageFilters";

const StatisticsInlineFilters = ({
  periodDisplayLabel,
  periodFilter,
  periodHistoryStart,
  periodHistoryStop,
  onApplyPeriod,
  fluxOptions = [],
  fluxFilter = "all",
  onApplyFlux,
  siteOptions = [],
  siteFilter = "all",
  onApplySite,
  addressOptions = [],
  addressFilter = "all",
  onApplyAddress,
  zoneOptions = [],
  zoneFilter = "all",
  onApplyZone,
  showZoneFilter = false,
  onClearFilters,
  siteLabel,
}) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(null);

  const displaySites = Boolean(onApplySite) && siteOptions.length > 0;
  const displayAddress = Boolean(onApplyAddress) && addressOptions.length > 0;
  const displayZones =
    showZoneFilter || (Boolean(onApplyZone) && zoneOptions.length > 0);

  const showClearFilters =
    typeof onClearFilters === "function" &&
    hasActiveClearableFilters([
      { value: fluxFilter, defaultValue: "all" },
      ...(displaySites ? [{ value: siteFilter, defaultValue: "all" }] : []),
      ...(displayAddress
        ? [{ value: addressFilter, defaultValue: "all" }]
        : []),
      ...(displayZones ? [{ value: zoneFilter, defaultValue: "all" }] : []),
    ]);

  return (
    <>
      <TableFilterBar
        className="activity-page-filter-bar"
        filters={
          <>
            <TableFilterButton
              label={t("Period")}
              value={periodDisplayLabel || t("Last 6 months")}
              onClick={() => setOpenModal("period")}
            />
            <TableFilterButton
              label={t("Flux")}
              value={getFilterLabel(fluxOptions, fluxFilter, t("All"))}
              onClick={() => setOpenModal("flux")}
            />
            {displaySites ? (
              <TableFilterButton
                label={siteLabel || t("Sites")}
                value={getFilterLabel(siteOptions, siteFilter, t("All"))}
                onClick={() => setOpenModal("sites")}
              />
            ) : null}
            {displayAddress ? (
              <TableFilterButton
                label={t("Address")}
                value={getFilterLabel(addressOptions, addressFilter, t("All"))}
                onClick={() => setOpenModal("address")}
              />
            ) : null}
            {displayZones ? (
              <TableFilterButton
                label={t("Zones")}
                value={getFilterLabel(zoneOptions, zoneFilter, t("All"))}
                onClick={() => setOpenModal("zones")}
              />
            ) : null}
            <TableFilterClearLink
              visible={showClearFilters}
              onClear={onClearFilters}
            />
          </>
        }
      />

      <PeriodFilterModal
        isOpen={openModal === "period"}
        onClose={() => setOpenModal(null)}
        onApply={(months, label, startDateIso, endDateIso, presetId) => {
          onApplyPeriod(months, label, startDateIso, endDateIso, presetId);
        }}
        currentPeriodFilter={periodFilter}
        currentPeriodStart={periodHistoryStart}
        currentPeriodEnd={periodHistoryStop}
      />

      <SingleSelectFilterModal
        isOpen={openModal === "flux"}
        onClose={() => setOpenModal(null)}
        title={t("Flux")}
        options={fluxOptions}
        currentValue={fluxFilter}
        onApply={onApplyFlux}
      />

      {displaySites ? (
        <SingleSelectFilterModal
          isOpen={openModal === "sites"}
          onClose={() => setOpenModal(null)}
          title={siteLabel || t("Sites")}
          options={siteOptions}
          currentValue={siteFilter}
          onApply={onApplySite}
        />
      ) : null}

      {displayAddress ? (
        <SingleSelectFilterModal
          isOpen={openModal === "address"}
          onClose={() => setOpenModal(null)}
          title={t("Address")}
          options={addressOptions}
          currentValue={addressFilter}
          onApply={onApplyAddress}
        />
      ) : null}

      {displayZones ? (
        <SingleSelectFilterModal
          isOpen={openModal === "zones"}
          onClose={() => setOpenModal(null)}
          title={t("Zones")}
          options={zoneOptions}
          currentValue={zoneFilter}
          onApply={onApplyZone}
        />
      ) : null}
    </>
  );
};

export default StatisticsInlineFilters;
